import React, { useEffect, useState } from 'react';
import { GenerateSpendCubeContext } from '.';
import {
	getAppState,
	getEtlState,
	getUploadPath,
	postUploadedUrls,
	restartProcess,
	uploadFile,
} from 'utils/apiRequests';
import { useToastMessageContext } from 'contexts/ToastMessageContext/ToastMessageProvider';
import { NotificationTypes } from 'components/Toast/constants';
import { ETL_PROCESS_STATUS } from 'Data/constants';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { ApSignalrConsumer } from '@alixpartners/ui-utils';

const GenerateSpendCubeContextProvider = ({ children }) => {
	const { showToast } = useToastMessageContext();
	const {
		NO_DATA,
		UNCLASSIFIED_FILE_GENERATING,
		UNCLASSIFIED_FILE_GENERATION_FAILED,
		CLASSIFICATION_SUCCEEDED,
		NORMALIZATION_SUCCEEDED,
	} = ETL_PROCESS_STATUS;
	const history = useHistory();
	const [currentState, setCurrentState] = useState(NO_DATA);
	const [finalMapping, setFinalMapping] = useState({ excels: [] });
	const [type, setType] = useState(1);
	const [pageLoading, setPageLoading] = useState(false);
	const [dataForMapping, setDataForMapping] = useState();
	const [isAutomation, setIsAutomation] = useState(true);
	const [isShowRestartModal, setIsShowRestartModal] = useState(false);
	const [isETL, setIsETL] = useState(null);
	const [isShowRedirectionMessage, setIsShowRedirectionMessage] = useState(
		false
	);

	const handleEtlStateLogic = (newState, newIsETL) => {
		if (
			newState === UNCLASSIFIED_FILE_GENERATION_FAILED ||
			newState === UNCLASSIFIED_FILE_GENERATING
		) {
			localStorage.removeItem('fileData');
			localStorage.removeItem('mapping');
		} else if (newState === NORMALIZATION_SUCCEEDED && newIsETL === 0) {
			history.push(`/data/vendor-edit`);
			setIsShowRedirectionMessage(false);
			restart();
		} else if (newState === CLASSIFICATION_SUCCEEDED) {
			getEtlState();
			getAppState();
		}
	};

	const getCurrentEtlState = () => {
		getEtlState().then(res => {
			const newState = res?.data?.state;
			const newIsETL = res?.data?.isETL;
			setCurrentState(newState);
			setIsETL(newIsETL);
			setPageLoading(false);

			handleEtlStateLogic(newState, newIsETL);
		});
	};

	const restart = () => {
		setIsAutomation(true);
		setType(1);
		localStorage.removeItem('fileData');
		localStorage.removeItem('mapping');
		setFinalMapping({ excels: [] });
		setIsShowRestartModal(false);
		setPageLoading(true);
		setIsShowRedirectionMessage(false);
		restartProcess()
			.then(res => {
				getCurrentEtlState();
			})
			.catch(err => {
				console.error(err);
				showToast(
					'Process could not be restarted.',
					NotificationTypes.danger,
					3000
				);
				setPageLoading(false);
			});
	};

	useEffect(() => {
		getCurrentEtlState();
		const columnMapping = JSON.parse(localStorage.getItem('mapping')) || {
			excels: [],
		};
		const mappingData = JSON.parse(localStorage.getItem('fileData')) || {};
		setFinalMapping(columnMapping);
		setDataForMapping(mappingData);
		//eslint-disable-next-line
	}, []);

	const onUploadClicked = (fileInfo, dataType) => {
		setPageLoading(true);
		const directIndirect = dataType === 2 ? 1 : dataType;
		setType(directIndirect);

		const fileList = fileInfo.map(file => {
			return file.selectedFileName;
		});

		const samplePayload = {
			Type: directIndirect,
			fileDetails: [],
		};

		const uploadedFiles = [];

		getUploadPath({ fileNames: fileList, type: directIndirect })
			.then(res => {
				if (res?.data?.length !== 0 && res?.succeeded) {
					getCurrentEtlState();
					fileInfo.forEach((element, index) => {
						const fileToUpload = element?.file[0];
						uploadFile(res.data[index]?.url, fileToUpload).then(res => {
							uploadedFiles.push(res.etag);
							if (uploadedFiles.length === fileInfo.length) {
								postUploadedUrls(samplePayload).then(res => {
									getCurrentEtlState();
								});
							}
						});
					});

					fileList.forEach((element, index) => {
						samplePayload.fileDetails.push({
							FileName: element,
							Url: res.data[index]?.url,
						});
					});
				} else {
					console.log('Error fetching urls');
					getCurrentEtlState();
				}
			})
			.catch(err => {
				console.error(err);
				showToast('File upload failed', NotificationTypes.danger, 3000);
			});
	};

	const updateMapping = (excelName, sheetName, newMapping) => {
		setFinalMapping(prevMapping => {
			let updatedFiles = [...prevMapping.excels];
			let fileIndex = updatedFiles.findIndex(
				file => file.excelName === excelName
			);

			if (fileIndex === -1) {
				updatedFiles.push({
					excelName,
					sheets: [{ sheetName, mapping: newMapping }],
				});
			} else {
				let sheetIndex = updatedFiles[fileIndex].sheets.findIndex(
					sheet => sheet.sheetName === sheetName
				);

				if (sheetIndex === -1) {
					updatedFiles[fileIndex].sheets.push({
						sheetName,
						mapping: newMapping,
					});
				} else {
					updatedFiles[fileIndex].sheets[sheetIndex].mapping = newMapping;
				}
			}

			const completedMapping = { excels: updatedFiles };
			localStorage.setItem('mapping', JSON.stringify(completedMapping));

			showToast('Mapping saved successfully.', NotificationTypes.success, 3000);
			return { excels: updatedFiles };
		});
	};

	const getMapping = (excelName, sheetName) => {
		const file = finalMapping.excels.find(f => f.excelName === excelName);
		const sheet = file?.sheets.find(s => s.sheetName === sheetName);
		return sheet?.mapping || [];
	};

	return (
		<React.Fragment>
			<ApSignalrConsumer
				message="UploadCompleted"
				onMessage={() => {
					getCurrentEtlState();
				}}
			/>
			<ApSignalrConsumer
				message="NormalizationCompleted"
				onMessage={() => {
					getCurrentEtlState();
					if (isETL === 0) {
						history.push(`/data/vendor-edit`);
						setIsShowRedirectionMessage(false);
					}
				}}
			/>
			<ApSignalrConsumer
				message="NormalizationInProgress"
				onMessage={(key, statusMessage) => {
					if (statusMessage === 'File published successfully.') {
						setIsShowRedirectionMessage(true);
						getEtlState();
					} else if (statusMessage === 'File validating') {
						getCurrentEtlState();
					}
				}}
			/>
			<ApSignalrConsumer
				message="UploadProcessing"
				onMessage={(key, message) => {
					if (
						message === 'Starting spend cube generation' ||
						message === 'Starting auto classification'
					) {
						getCurrentEtlState();
						setIsShowRedirectionMessage(false);
						history.push(`/data/append`);
					}
				}}
			/>
			<ApSignalrConsumer
				message="ClassificationSucceeded"
				onMessage={() => {
					getCurrentEtlState();
				}}
			/>
			<GenerateSpendCubeContext.Provider
				value={{
					currentState,
					setCurrentState,
					finalMapping,
					updateMapping,
					getMapping,
					onUploadClicked,
					type,
					setType,
					getCurrentEtlState,
					pageLoading,
					setPageLoading,
					dataForMapping,
					isAutomation,
					setIsAutomation,
					restart,
					isShowRestartModal,
					setIsShowRestartModal,
					isETL,
					isShowRedirectionMessage,
					setIsShowRedirectionMessage,
				}}
			>
				{children}
			</GenerateSpendCubeContext.Provider>
		</React.Fragment>
	);
};

export default GenerateSpendCubeContextProvider;
